import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CardElement, Elements, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";

import InfoTooltip from "../components/common/InfoTooltip";
import { useAuth } from "../context/AuthContext";
import {
  createBillingSetupIntent,
  getBillingSummary,
  setDefaultPaymentMethod,
} from "../api/billing";

const MODAL_TEXT = "text-sm md:text-base";
const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "";
const stripePromise = STRIPE_PUBLISHABLE_KEY ? loadStripe(STRIPE_PUBLISHABLE_KEY) : null;

const CARD_ELEMENT_OPTIONS = {
  hidePostalCode: true,
  style: {
    base: {
      fontSize: "14px",
      color: "#0f172a",
      "::placeholder": {
        color: "#94a3b8",
      },
      fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
    },
    invalid: {
      color: "#dc2626",
    },
  },
};

const formatBillingDateTime = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(parsed);
};

const pluralizeDay = (value) => `${value} ${Number(value) === 1 ? "day" : "days"}`;

function ReplaceCardForm() {
  const { token, updateCurrentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const stripe = useStripe();
  const elements = useElements();

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [nameOnCard, setNameOnCard] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [city, setCity] = useState("");
  const [province, setProvince] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [country, setCountry] = useState("CA");
  const [trialSummary, setTrialSummary] = useState(null);
  const [trialLoading, setTrialLoading] = useState(false);
  const [trialError, setTrialError] = useState("");

  const billingDetails = useMemo(
    () => ({
      name: nameOnCard || undefined,
      address: {
        line1: addressLine1 || undefined,
        line2: addressLine2 || undefined,
        city: city || undefined,
        state: province || undefined,
        postal_code: postalCode || undefined,
        country: country || undefined,
      },
    }),
    [nameOnCard, addressLine1, addressLine2, city, province, postalCode, country]
  );
  const searchParams = useMemo(
    () => new URLSearchParams(location.search || ""),
    [location.search]
  );
  const onboardingMode = searchParams.get("onboarding") === "1";
  const nextPath = (searchParams.get("next") || "").trim();
  const skipDestination = nextPath || "/setup";

  useEffect(() => {
    if (!token) {
      setTrialSummary(null);
      setTrialLoading(false);
      setTrialError("");
      return undefined;
    }
    let isMounted = true;
    setTrialLoading(true);
    setTrialError("");
    getBillingSummary(token)
      .then((data) => {
        if (!isMounted) return;
        setTrialSummary(data || null);
      })
      .catch((err) => {
        if (!isMounted) return;
        setTrialError(
          err?.response?.data?.detail ||
            err?.message ||
            "Unable to load trial details."
        );
      })
      .finally(() => {
        if (!isMounted) return;
        setTrialLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [token]);

  const trialTotalDays = Number(trialSummary?.trial_total_days || 30);
  const trialDaysUsed = Number(trialSummary?.trial_days_used || 0);
  const trialDaysRemaining = Number(trialSummary?.trial_days_remaining || 0);
  const trialEndsLabel = formatBillingDateTime(
    trialSummary?.trial_end_utc || trialSummary?.current_period_end_utc
  );
  const billingTestFree =
    Boolean(trialSummary?.billing_test_free) ||
    (trialSummary?.status || "").toLowerCase() === "test_free";
  const billingSkipAllowed =
    onboardingMode && (billingTestFree || Boolean(trialSummary?.billing_skip_allowed));

  const handleSkipBilling = () => {
    if (trialSummary?.status) {
      updateCurrentUser({
        billing_status: String(trialSummary.status || "").toLowerCase(),
        billing_access_granted: true,
        billing_card_required: false,
      });
    }
    navigate(skipDestination, { replace: true });
  };

  if (!trialLoading && billingTestFree) {
    return (
      <div className="space-y-6">
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          {trialSummary?.billing_free_reason ||
            "Testing account - billing is waived. No card or subscription is required for this salon."}
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 lg:p-6 space-y-4">
          <div>
            <h2 className="text-lg md:text-xl font-semibold text-slate-900">
              Billing information not required
            </h2>
            <p className="text-sm text-slate-500">
              This testing salon can keep using Ellyanna without adding a payment card.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => {
                if (onboardingMode) {
                  handleSkipBilling();
                  return;
                }
                navigate("/manager/manage/billing");
              }}
              className="rounded-full bg-slate-900 px-4 py-2 text-xs font-semibold text-white hover:bg-slate-800"
            >
              {onboardingMode ? "Continue" : "Back to Billing"}
            </button>
            {!onboardingMode && (
              <button
                type="button"
                onClick={() => navigate("/manager/schedule")}
                className="rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
              >
                Back to Schedule
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) {
      setError("Card form is still loading. Please wait a moment and try again.");
      return;
    }
    if (!nameOnCard.trim()) {
      setError("Name on card is required.");
      return;
    }
    setSubmitting(true);
    setError("");
    setNotice("");
    try {
      const setup = await createBillingSetupIntent(token, {
        billing_details: billingDetails,
      });
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        throw new Error("Card input is unavailable.");
      }

      const result = await stripe.confirmCardSetup(setup.client_secret, {
        payment_method: {
          card: cardElement,
          billing_details: billingDetails,
        },
      });
      if (result.error) {
        throw new Error(result.error.message || "Unable to confirm card setup.");
      }

      const paymentMethodId = result?.setupIntent?.payment_method;
      if (!paymentMethodId || typeof paymentMethodId !== "string") {
        throw new Error("Card setup did not return a payment method.");
      }

      const billingResult = await setDefaultPaymentMethod(token, {
        payment_method_id: paymentMethodId,
        billing_details: billingDetails,
      });

      const normalizedStatus = String(billingResult?.status || "").toLowerCase();
      const billingAccessGranted = ["active", "trialing"].includes(normalizedStatus);
      updateCurrentUser({
        billing_status: normalizedStatus || null,
        billing_access_granted: billingAccessGranted,
        billing_card_required: !billingAccessGranted,
      });

      const trialEndLabel = formatBillingDateTime(
        billingResult?.trial_end_utc || billingResult?.current_period_end_utc
      );
      setNotice(
        onboardingMode
          ? trialEndLabel
            ? `Card saved. Billing will begin after your free trial ends on ${trialEndLabel}.`
            : "Card saved. Billing is ready for your subscription."
          : "Card replaced successfully."
      );
      setTimeout(() => {
        if (onboardingMode) {
          navigate(nextPath || "/setup", { replace: true });
          return;
        }
        navigate("/manager/manage/billing/card");
      }, onboardingMode ? 2000 : 600);
    } catch (err) {
      setError(
        err?.response?.data?.detail || err?.message || "Unable to replace card."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}
      {notice && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          {notice}
        </div>
      )}

      <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 lg:p-6 space-y-4">
        <div>
          <h2 className="text-lg md:text-xl font-semibold text-slate-900">
            {onboardingMode ? "Add Billing Information" : "Replace Card"}
          </h2>
          <p className="text-sm text-slate-500">
            {onboardingMode
              ? "Enter your card details to continue after your free trial or to keep billing ready before the trial ends."
              : "Update the default card used for this salon's subscription billing."}
          </p>
        </div>

        {onboardingMode && (
          <div
            className={`rounded-2xl border px-4 py-3 text-sm ${
              trialSummary?.trial_has_ended
                ? "border-amber-200 bg-amber-50 text-amber-900"
                : trialError
                ? "border-rose-200 bg-rose-50 text-rose-700"
                : "border-sky-200 bg-sky-50 text-sky-900"
            }`}
          >
            {trialLoading ? (
              "Checking your free trial details..."
            ) : trialError ? (
              <>
                {trialError} Add billing information now, or return to the billing page
                and try again.
              </>
            ) : trialSummary?.trial_has_ended ? (
              "Your 30-day free trial has ended. Add billing information to continue using Ellyanna."
            ) : trialSummary ? (
              <>
                You have used {pluralizeDay(trialDaysUsed)} of your{" "}
                {pluralizeDay(trialTotalDays)} free trial.{" "}
                {trialDaysRemaining > 0
                  ? `${pluralizeDay(trialDaysRemaining)} remaining. `
                  : ""}
                {trialEndsLabel ? `Your trial ends on ${trialEndsLabel}. ` : ""}
                You can save billing information now or skip this step until the trial
                ends.
              </>
            ) : (
              "New salons can use Ellyanna for 30 days before adding a card. If your trial is still active, billing will begin after the trial end date shown on the billing page."
            )}
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <label className="space-y-1">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Name on card
            </span>
            <input
              type="text"
              value={nameOnCard}
              onChange={(e) => setNameOnCard(e.target.value)}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
              placeholder="Cardholder name"
              required
            />
          </label>
        </div>

        <div className="space-y-2">
          <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Card details
            <InfoTooltip
              text="Secure card fields are submitted directly through the payment provider."
              label="Card Details"
            />
          </div>
          <div className="rounded-xl border border-slate-200 px-3 py-3">
            <CardElement options={CARD_ELEMENT_OPTIONS} />
          </div>
          <div className="inline-flex items-center gap-2 text-xs text-slate-500">
            CVV
            <InfoTooltip
              label="What's this?"
              text="CVV is the 3-digit security code on the back of your card (Amex: 4 digits on front)."
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <label className="space-y-1">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Billing address line 1
            </span>
            <input
              type="text"
              value={addressLine1}
              onChange={(e) => setAddressLine1(e.target.value)}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
              placeholder="Optional"
            />
          </label>
          <label className="space-y-1">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Billing address line 2
            </span>
            <input
              type="text"
              value={addressLine2}
              onChange={(e) => setAddressLine2(e.target.value)}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
              placeholder="Optional"
            />
          </label>
          <label className="space-y-1">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              City
            </span>
            <input
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
              placeholder="Optional"
            />
          </label>
          <label className="space-y-1">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Province / State
            </span>
            <input
              type="text"
              value={province}
              onChange={(e) => setProvince(e.target.value)}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
              placeholder="Optional"
            />
          </label>
          <label className="space-y-1">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Postal code
            </span>
            <input
              type="text"
              value={postalCode}
              onChange={(e) => setPostalCode(e.target.value)}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
              placeholder="Optional"
            />
          </label>
          <label className="space-y-1">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Country
            </span>
            <input
              type="text"
              value={country}
              onChange={(e) => setCountry(e.target.value.toUpperCase())}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
              placeholder="CA"
              maxLength={2}
            />
          </label>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() =>
              navigate(
                onboardingMode
                  ? "/manager/manage/billing"
                  : "/manager/manage/billing/card"
              )
            }
            className="rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
          >
            Back
          </button>
          <button
            type="submit"
            disabled={submitting || !stripe}
            className="rounded-full bg-slate-900 px-4 py-2 text-xs font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
          >
            {submitting
              ? onboardingMode
                ? "Saving..."
                : "Replacing..."
              : onboardingMode
              ? "Save Billing Info"
              : "Replace Card"}
          </button>
          {billingSkipAllowed && (
            <button
              type="button"
              onClick={handleSkipBilling}
              className="rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
            >
              Skip for now
            </button>
          )}
        </div>
      </div>
    </form>
  );
}

export default function BillingReplaceCard() {
  const navigate = useNavigate();
  const location = useLocation();
  const onboardingMode = useMemo(() => {
    const searchParams = new URLSearchParams(location.search || "");
    return searchParams.get("onboarding") === "1";
  }, [location.search]);

  return (
    <div className="manageSectionShell">
      <div
        className={`manageSectionWide manage-section-wide manage-section-compact ${MODAL_TEXT}`}
      >
        <div className="rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4">
            <div className="flex items-center justify-between gap-3">
              <h1 className="text-xl md:text-2xl font-semibold text-white">
                {onboardingMode ? "Add Billing Information" : "Replace Card"}
              </h1>
              <button
                type="button"
                onClick={() => navigate(-1)}
                aria-label="Close and go back"
                className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-white/45 bg-white/10 text-xl font-semibold leading-none text-white transition hover:bg-white/20"
              >
                X
              </button>
            </div>
          </div>

          <div className="px-6 py-6 space-y-6">
            {!stripePromise && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                Billing card setup is unavailable because the publishable key is not configured.
              </div>
            )}
            {stripePromise ? (
              <Elements stripe={stripePromise}>
                <ReplaceCardForm />
              </Elements>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => navigate("/manager/manage/billing")}
                  className="rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Back
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
